import { useState } from 'react';
import {
  useTranslate,
  useNotification,
  useCustom,
  useCustomMutation,
} from '@refinedev/core';
import { Modal, Spin, Button, Empty } from 'antd';
import type { PublicationDiffOut } from '@/api/generated/schemas';
import { ImportDiffTable } from './ImportDiffTable';

interface ImportDiffModalProps {
  open: boolean;
  onClose: () => void;
  publicationId: number;
  onSuccess?: () => void;
}

export const ImportDiffModal = ({
  open,
  onClose,
  publicationId,
  onSuccess,
}: ImportDiffModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [submitting, setSubmitting] = useState(false);
  const [overrideManual, setOverrideManual] = useState(false);
  const { query } = useCustom<PublicationDiffOut>({
    url: `user_catalog/publications/${publicationId}/import-diff`,
    method: 'get',
    queryOptions: {
      enabled: open,
    },
  });

  const { data, isLoading } = query;
  const fields = data?.data?.fields ?? [];

  const handleImport = async () => {
    setSubmitting(true);
    try {
      await mutateAsync({
        url: `user_catalog/publications/${publicationId}/import-apply`,
        method: 'post',
        values: { override: overrideManual }, // Atomic import
        successNotification: false,
        errorNotification: false,
      });

      notify?.({
        type: 'success',
        message: t('user_catalog.publications.import.success_title'),
        description: t('user_catalog.publications.import.success_notification'),
      });

      onSuccess?.();
      handleClose();
    } catch {
      notify?.({
        type: 'error',
        message: t('user_catalog.publications.import.failed_title'),
        description: t('user_catalog.publications.import.failed_notification'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasDiffs = fields.length > 0;

  const handleClose = () => {
    setOverrideManual(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      title={t('user_catalog.publications.import.modal_title')}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={submitting}>
          {t('actions.cancel')}
        </Button>,
        hasDiffs && (
          <Button
            key="import"
            type="primary"
            onClick={handleImport}
            loading={submitting}
            disabled={isLoading}
          >
            {t('user_catalog.publications.import.import_all')}
          </Button>
        ),
      ]}
      width={900}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : hasDiffs ? (
        <ImportDiffTable
          fields={fields}
          overrideManual={overrideManual}
          onOverrideManualChange={setOverrideManual}
        />
      ) : (
        <div style={{ padding: '40px 0' }}>
          <Empty
            description={t('user_catalog.publications.import.empty_message')}
          />
        </div>
      )}
    </Modal>
  );
};
